import { useMemo } from 'react'
import { Surgery } from 'common/types'
import { TProcedureFilters } from 'lib/hooks/useFilters'

// Hospital name constants (consistent with existing logic)
const HOSPITAL_NAMES = [
  'General',
  'Community',
  'Childrens',
  'UCLA',
  'USC',
] as const

/**
 * Generates a deterministic hospital name based on surgery ID
 * This ensures the same surgery always gets the same hospital name
 * @param surgeryId - The unique identifier for the surgery
 * @returns A consistent hospital name for the given surgery
 */
const getHospitalNameForSurgery = (surgeryId: string): string => {
  let hash = 0
  for (let i = 0; i < surgeryId.length; i++) {
    const char = surgeryId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  const index = Math.abs(hash) % HOSPITAL_NAMES.length
  return HOSPITAL_NAMES[index]
}

export interface UseSurgeryGroupingProps {
  surgeries: Surgery[]
  filters: TProcedureFilters
}

export interface SurgeryGroup {
  hospitalName: string
  surgeries: Surgery[]
  count: number
}

export interface UseSurgeryGroupingReturn {
  groupedSurgeries: Map<string, Surgery[]>
  hospitalNames: string[]
  surgeryGroups: SurgeryGroup[]
  totalSurgeries: number
  hasFilteredResults: boolean
}

/**
 * Custom hook to group surgeries by hospital and apply filtering
 * Returns grouped data and metadata for the grouped procedure list
 */
export const useSurgeryGrouping = ({
  surgeries,
  filters,
}: UseSurgeryGroupingProps): UseSurgeryGroupingReturn => {
  const groupedData = useMemo(() => {
    if (!surgeries || surgeries.length === 0) {
      return {
        groups: new Map<string, Surgery[]>(),
        totalCount: 0,
      }
    }

    const groups = new Map<string, Surgery[]>()
    let totalCount = 0

    surgeries.forEach((surgery) => {
      const hospitalName = getHospitalNameForSurgery(surgery._id)

      // Apply hospital filter if active
      if (filters.rooms.length > 0 && !filters.rooms.includes(hospitalName)) {
        return // Skip this surgery if hospital filter is active and doesn't match
      }

      if (!groups.has(hospitalName)) {
        groups.set(hospitalName, [])
      }
      groups.get(hospitalName)!.push(surgery)
      totalCount++
    })

    // Sort hospitals alphabetically
    const sortedGroups = new Map([...groups.entries()].sort())

    return {
      groups: sortedGroups,
      totalCount,
    }
  }, [surgeries, filters.rooms])

  const hospitalNames = useMemo(() => {
    const names = Array.from(groupedData.groups.keys()).sort()
    return names
  }, [groupedData.groups])

  const surgeryGroups = useMemo(() => {
    return hospitalNames.map((hospitalName) => ({
      hospitalName,
      surgeries: groupedData.groups.get(hospitalName) || [],
      count: groupedData.groups.get(hospitalName)?.length || 0,
    }))
  }, [hospitalNames, groupedData.groups])

  const hasFilteredResults = useMemo(() => {
    return groupedData.totalCount > 0
  }, [groupedData.totalCount])

  return {
    groupedSurgeries: groupedData.groups,
    hospitalNames,
    surgeryGroups,
    totalSurgeries: groupedData.totalCount,
    hasFilteredResults,
  }
}

// Export the utility function for use in other components
export { getHospitalNameForSurgery }
